import { Box, Checkbox, ClickAwayListener, Collapse } from '@mui/material'
import { ReactComponent as NextIcon } from 'assets/arrow_close_logo.svg'
import { ReactComponent as CrossCloseLogo } from 'assets/cross_close_logo.svg'
import { ReactComponent as CheckBox } from 'assets/icons/checkBox.svg'
import CheckBoxActive from 'assets/icons/checkBoxActive'
import { useEffect, useRef, useState } from 'react'
import { COLORS } from 'styles/colors'
import { Scrollbar } from 'ui/scrollbar/Scrollbar'

import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'

interface SelectedType {
	type: number
	value: number
}
interface SelectSmallProps {
	typeArray: any
	selectedArray?: any
	state?: any
	placeholder?: string
	isCheckbox?: boolean
	height?: string
	width?: string
	value?: string[] | number[] | string | number
	isMultiplaySelect?: boolean
	isClearIcon?: boolean
	startAdornmentDesc?: string
	onClick?: (type: number) => void
	scrollbarColor?: string
	floatingLabel?: string
	emptyLabel?: string
	disableDeselect?: boolean
	closeOnSelect?: boolean
	menuPlacement?: 'top' | 'bottom'
	menuInFlow?: boolean
	showSelectedCount?: boolean
}

export default function SelectSmall(props: SelectSmallProps) {
	const {
		typeArray,
		state,
		placeholder,
		isCheckbox,
		height,
		isMultiplaySelect,
		isClearIcon,
		value,
		startAdornmentDesc,
		width,
		onClick,
		scrollbarColor = COLORS.main,
		floatingLabel,
		selectedArray,
		emptyLabel,
		disableDeselect,
		closeOnSelect,
		menuPlacement = 'bottom',
		menuInFlow = false,
		showSelectedCount = false,
	} = props
	const [openSelectedList, setOpenSelectedList] = useState<boolean>(false)
	const [selected, setSelected] = useState<SelectedType[]>([])
	const [selectedCheckbox, setSelectedCheckbox] = useState<number[]>([])
	const menuRef = useRef<HTMLDivElement | null>(null)
	const showFloatingLabel =
		Boolean(floatingLabel) &&
		(selectedCheckbox.length > 0 || Boolean(emptyLabel))
	const emptyDisplayText = emptyLabel
		? emptyLabel
		: placeholder
			? placeholder
			: 'Тип'

	const toggleCheckbox = (type, value) => {
		if (selectedCheckbox.includes(type)) {
			if (disableDeselect) {
				if (closeOnSelect) setOpenSelectedList(false)
				return
			}
			setSelectedCheckbox(selectedCheckbox.filter(item => item !== type))
			setSelected(selected.filter(item => item.type !== type))
			if (state) {
				state(prevState => {
					const newState = prevState.filter(item => item !== type)
					return newState
				})
			}
		} else {
			setSelectedCheckbox(
				isMultiplaySelect ? [...selectedCheckbox, type] : [type],
			)
			setSelected(
				isMultiplaySelect ? [...selected, { type, value }] : [{ type, value }],
			)
			if (state) {
				state(prevState => {
					const newState = isMultiplaySelect ? [...prevState, type] : [type]
					return newState
				})
			}
			if (onClick) {
				onClick(type)
			}
			if (closeOnSelect) {
				setOpenSelectedList(false)
			}
		}
	}
	useEffect(() => {
		if (value) {
			const matchEl = typeArray.find(el => el.type === value)
			if (matchEl) {
				setSelected([matchEl])
				setSelectedCheckbox([matchEl.type])
			}
		} else if (!selectedArray) {
			setSelected([])
			setSelectedCheckbox([])
		}
	}, [value])

	useEffect(() => {
		if (!selectedArray) return
		setSelectedCheckbox(selectedArray)
		setSelected(
			typeArray
				.filter(el => selectedArray.includes(el.type))
				.map(el => ({ type: el.type, value: el.value })),
		)
	}, [selectedArray, typeArray])

	const clearSelectedItem = () => {
		setSelectedCheckbox([])
		setSelected([])
		if (state) {
			state([])
		}
	}

	const hasSelection = selectedCheckbox.length > 0
	const isAllSelected =
		selectedCheckbox.length === typeArray.length ||
		(selectedCheckbox.length === 1 &&
			typeArray.find(el => el.type === selectedCheckbox[0])?.value === 'Все')
	const showClearIcon = hasSelection && !isAllSelected

	return (
		<ClickAwayListener onClickAway={() => setOpenSelectedList(false)}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					gap: '8px',
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box
						onClick={() => setOpenSelectedList(prevState => !prevState)}
						sx={{
							minWidth: width ? width : '100px',
							maxWidth: startAdornmentDesc || floatingLabel ? width : '150px',
							width: startAdornmentDesc || floatingLabel ? width : undefined,
							height: height ? height : '30px',
							borderRadius: '8px',
							border: `1px solid ${
								openSelectedList
									? COLORS.main
									: floatingLabel && selectedCheckbox.length > 0
										? COLORS.icon
										: COLORS.inputBorder
							}`,
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '4px',
							cursor: 'pointer',
							padding: '4px 32px 4px 8px',
							whiteSpace: 'nowrap',
							position: 'relative',
							'&:hover': {
								border: `1px solid ${
									openSelectedList ? COLORS.main : COLORS.black
								}`,
								boxShadow: 'none',
							},
						}}
					>
						{showFloatingLabel && (
							<TypographySecondary
								sx={{
									position: 'absolute',
									top: '-8px',
									left: '8px',
									px: '3px',
									backgroundColor: COLORS.white,
									fontSize: '12px',
									lineHeight: '14px',
								}}
							>
								{floatingLabel}
							</TypographySecondary>
						)}
						{startAdornmentDesc ? (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '4px',
									width: '100%',
									minWidth: 0,
									overflow: 'hidden',
								}}
							>
								{selectedCheckbox.length !== 0 && (
									<TypographySecondary>
										{startAdornmentDesc}
									</TypographySecondary>
								)}
								{selectedCheckbox.length === 0 ? (
									<TypographySecondary>
										{placeholder ? placeholder : 'Тип'}
									</TypographySecondary>
								) : (
									<TypographyPrimary
										sx={{
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										}}
									>
										{selected[0]?.value}
									</TypographyPrimary>
								)}
							</Box>
						) : (
							<>
								<Box sx={{ flex: 1, overflow: 'hidden' }}>
									{selectedCheckbox.length === 0 ? (
										emptyLabel ? (
											<TypographyPrimary>{emptyDisplayText}</TypographyPrimary>
										) : (
											<TypographySecondary>
												{emptyDisplayText}
											</TypographySecondary>
										)
									) : (
										<Box
											sx={{
												display: 'flex',
												flexDirection: 'row',
												alignItems: 'center',
												gap: '4px',
												overflow: 'hidden',
											}}
										>
											<TypographyPrimary
												sx={{
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												{showSelectedCount && selected.length > 1
													? `(${selected.length}) ${selected
															.map(item => item.value)
															.join(', ')}`
													: selectedCheckbox.length === typeArray.length
														? 'Все'
														: selected[0]?.value}
											</TypographyPrimary>
											{selected.length > 1 &&
												!showSelectedCount &&
												selectedCheckbox.length !== typeArray.length && (
													<TypographyPrimary sx={{ whiteSpace: 'nowrap' }}>
														+{selected.length - 1}
													</TypographyPrimary>
												)}
										</Box>
									)}
								</Box>
							</>
						)}
						<NextIcon
							stroke={COLORS.icon}
							style={{
								transform: `translateY(-50%) rotate(${openSelectedList ? '180' : '0'}deg)`,
								stroke: openSelectedList && COLORS.main,
								width: '16px',
								height: '16px',
								flexShrink: 0,
								position: 'absolute',
								right: '8px',
								top: '50%',
							}}
						/>
					</Box>
					<Collapse
						in={openSelectedList}
						timeout='auto'
						sx={{ zIndex: 9999999 }}
						onEntered={() => {
							if (menuInFlow) {
								menuRef.current?.scrollIntoView({
									block: 'nearest',
									behavior: 'smooth',
								})
							}
						}}
					>
						<Box sx={{ position: 'relative' }}>
							<Box
								ref={menuRef}
								sx={{
									position: menuInFlow ? 'static' : 'absolute',
									top:
										!menuInFlow && menuPlacement === 'bottom'
											? '8px'
											: undefined,
									bottom:
										!menuInFlow && menuPlacement === 'top' ? '48px' : undefined,
									mt: menuInFlow ? '8px' : 0,
									minWidth: '100%',
									display: 'flex',
									flexDirection: 'column',
									gap: '6px',
									background: '#fff',
									borderRadius: '8px',
									boxShadow:
										openSelectedList && '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
								}}
							>
								<Scrollbar
									sx={{ maxHeight: '180px' }}
									scrollbarColor={scrollbarColor}
								>
									{typeArray.map((el, index) => (
										<Box
											onClick={() => toggleCheckbox(el.type, el.value)}
											key={index}
											sx={{
												display: 'flex',
												flexDirection: 'row',
												alignItems: 'center',
												justifyContent: isCheckbox ? 'start' : 'center',
												padding: '3px 4px',
												gap: '4px',
												cursor: 'pointer',
												'&:hover': {
													backgroundColor: COLORS.mainSecondaryOpacity,
												},
											}}
										>
											{isCheckbox && (
												<Checkbox
													sx={{
														color: COLORS.inputBorder,
														'&.MuiCheckbox-root': {
															margin: '0px',
															padding: '0px',
															width: '20px',
															height: '20px',
															'&.Mui-checked': {
																color: COLORS.main,
															},
														},
													}}
													disableRipple
													icon={<CheckBox />}
													checkedIcon={<CheckBoxActive />}
													checked={selectedCheckbox.includes(el.type)}
												/>
											)}

											<TypographyPrimary sx={{ textAlign: 'left' }}>
												{el.value}
											</TypographyPrimary>
										</Box>
									))}
								</Scrollbar>
							</Box>
						</Box>
					</Collapse>
				</Box>
				{isClearIcon && (
					<Box
						sx={{
							width: '16px',
							height: '16px',
							flexShrink: 0,
							visibility: showClearIcon ? 'visible' : 'hidden',
							pointerEvents: showClearIcon ? 'auto' : 'none',
						}}
					>
						<CrossCloseLogo
							onClick={() => clearSelectedItem()}
							style={{
								width: '16px',
								height: '16px',
								cursor: 'pointer',
								stroke: COLORS.secondary,
							}}
						/>
					</Box>
				)}
			</Box>
		</ClickAwayListener>
	)
}
